import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Field, PasswordInput } from '@l2u/ui/components/Field.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { AuthSplitLayout, AuthNotice } from '@l2u/ui/components/AuthSplitLayout.tsx';
import { setAdminAccessToken } from '@l2u/ui/lib/auth/adminToken.ts';
import { getAdminSupabase } from '@l2u/ui/lib/supabase.ts';
import { MIN_PASSWORD_LENGTH, isPasswordValid } from '@l2u/shared/password';
import { PasswordRequirements } from '../components/PasswordRequirements.tsx';
import { friendlyAuthError } from '../lib/auth/friendlyAuthError.ts';

export function ResetPassword() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'redeeming' | 'set-password'>('redeeming');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    let supabase;
    try {
      supabase = getAdminSupabase();
    } catch (err) {
      setError(err);
      return;
    }

    const hash = window.location.hash.replace(/^#/, '');
    const fragment = hash ? new URLSearchParams(hash) : null;
    const query = new URLSearchParams(window.location.search);

    const access = fragment?.get('access_token');
    const refresh = fragment?.get('refresh_token');
    const code = query.get('code');
    const tokenHash = query.get('token_hash');

    const redeem = async (): Promise<string | null | undefined> => {
      if (access && refresh) {
        const { data, error: sessionErr } = await supabase.auth.setSession({
          access_token: access,
          refresh_token: refresh,
        });
        if (sessionErr) throw sessionErr;
        setAdminAccessToken(access);
        return data.user?.email ?? null;
      }
      if (code) {
        const { data, error: exchangeErr } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeErr) throw exchangeErr;
        setAdminAccessToken(data.session?.access_token ?? null);
        return data.user?.email ?? null;
      }
      if (tokenHash) {
        const { data, error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        });
        if (otpErr) throw otpErr;
        setAdminAccessToken(data.session?.access_token ?? null);
        return data.user?.email ?? null;
      }
      throw new Error('Reset link is missing the credentials needed to continue.');
    };

    void redeem()
      .then((resolvedEmail) => {
        window.history.replaceState({}, '', '/reset-password');
        setEmail(resolvedEmail ?? null);
        setPhase('set-password');
      })
      .catch((err) => setError(err));
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`));
      return;
    }
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      return;
    }
    setSubmitting(true);
    try {
      const supabase = getAdminSupabase();
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw updateErr;
      navigate('/', { replace: true });
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === 'redeeming') {
    if (error) {
      return (
        <AuthNotice
          variant="admin"
          title="This reset link didn’t work"
          error={error}
          action={
            <a
              href="/forgot-password"
              className="font-semibold text-brand-primary hover:underline"
            >
              Request a new reset link
            </a>
          }
        >
          It may have expired or already been used.
        </AuthNotice>
      );
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
        <CenteredSpinner />
        <p className="text-sm text-slate-500">Verifying your reset link…</p>
      </div>
    );
  }

  return (
    <AuthSplitLayout variant="admin">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">Reset your password</h2>
          <p className="text-sm text-slate-500">
            Enter a new password to finish resetting your account.
          </p>
          {email && <p className="text-sm font-medium text-slate-700">{email}</p>}
        </div>

        <form className="flex flex-col gap-5" onSubmit={onSubmit}>
          <Field label="New password" required hint={`At least ${MIN_PASSWORD_LENGTH} characters`}>
            <PasswordInput
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              className="h-12"
              style={{ borderRadius: 10 }}
            />
          </Field>
          <Field label="Confirm password" required>
            <PasswordInput
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              className="h-12"
              style={{ borderRadius: 10 }}
            />
          </Field>
          <ErrorMessage error={error} />
          <Btn
            type="submit"
            loading={submitting}
            disabled={!isPasswordValid(password) || password !== confirm}
            className="w-full"
            style={{ borderRadius: 10, height: 48 }}
          >
            Update password and sign in
          </Btn>
          <PasswordRequirements password={password} />
        </form>
      </div>
    </AuthSplitLayout>
  );
}
